import { apiClient } from './client';
import { PaginatedResponse } from '../types/pagination';
import { PersonalAppointmentStatus } from '../types/lead';

export type GoogleCalendarSyncStatus = 'NOT_CONNECTED' | 'SYNCED' | 'FAILED';

export interface PersonalAppointmentCounselorEntry {
  id: number;
  isPrimary: boolean;
  user: { id: number; firstName: string; lastName: string; email: string };
}

export interface PersonalAppointmentDetail {
  id: number;
  status: PersonalAppointmentStatus;
  appointmentDate: string | null;
  notes: string | null;
  createdAt: string;
  completedAt: string | null;
  completedBy: { id: number; firstName: string; lastName: string } | null;
  createdBy: { id: number; firstName: string; lastName: string };
  counselors: PersonalAppointmentCounselorEntry[];
  googleEventId: string | null;
  googleCalendarSyncStatus: GoogleCalendarSyncStatus | null;
  lead: {
    id: number;
    leadNumber: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string | null;
    leadSource: { id: number; name: string };
    assignedTo: { id: number; firstName: string; lastName: string } | null;
    branch: { id: number; name: string } | null;
    student: { id: number; studentNumber: string } | null;
  };
}

export interface ListPersonalAppointmentsParams {
  page: number;
  pageSize: number;
  status?: PersonalAppointmentStatus;
  branchId?: number;
}

export const listPersonalAppointments = async (
  params: ListPersonalAppointmentsParams,
): Promise<PaginatedResponse<PersonalAppointmentDetail>> => {
  const { data } = await apiClient.get('/personal-appointments', { params });
  return data;
};

export interface EligibleAppointmentCounselor {
  id: number;
  firstName: string;
  lastName: string;
}

/** "Assign Counselors" picker (Phase 77) — active Counselors in the
 * caller's own branch, backing the Convert-to-Personal-Appointment
 * dialog's multi-select. */
export const listEligibleAppointmentCounselors = async (): Promise<EligibleAppointmentCounselor[]> => {
  const { data } = await apiClient.get('/personal-appointments/eligible-counselors');
  return data.data;
};

export const createPersonalAppointment = async (payload: {
  leadId: number;
  appointmentDate?: string;
  notes?: string;
  counselorIds?: number[];
}): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.post('/personal-appointments', payload);
  return data.data;
};

export const getPersonalAppointment = async (id: number): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.get(`/personal-appointments/${id}`);
  return data.data;
};

export const updatePersonalAppointment = async (
  id: number,
  payload: {
    appointmentDate?: string | null;
    notes?: string | null;
    status?: 'SCHEDULED' | 'CANCELLED';
    counselorIds?: number[];
  },
): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.patch(`/personal-appointments/${id}`, payload);
  return data.data;
};

export const completePersonalAppointment = async (id: number): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.post(`/personal-appointments/${id}/complete`);
  return data.data;
};

// ---- Completed Consultation (2026-11-05) ----

/** A counselor as shown on Completed Consultation — name and login (their sign-in email) only. */
export interface ConsultationPerson {
  id: number;
  name: string;
  login: string;
}

export interface CompletedConsultationRow {
  id: number;
  appointmentId: string;
  status: PersonalAppointmentStatus;
  appointmentDate: string | null;
  completedAt: string | null;
  completedBy: ConsultationPerson | null;
  assignedCounselor: (ConsultationPerson & { isPrimary: boolean }) | null;
  otherCounselors: Array<ConsultationPerson & { isPrimary: boolean }>;
  lead: {
    id: number;
    leadNumber: string;
    name: string;
    phone: string;
    email: string | null;
    branch: { id: number; name: string } | null;
    student: { id: number; studentNumber: string } | null;
  };
  canReopen: boolean;
}

export interface CompletedConsultationFilters {
  search?: string;
  counselorId?: number;
  branchId?: number;
  status?: PersonalAppointmentStatus;
  appointmentFrom?: string;
  appointmentTo?: string;
  completedFrom?: string;
  completedTo?: string;
}

export interface CompletedConsultationSummary {
  total: number;
  completedToday: number;
  completedThisMonth: number;
  byCounselor: Array<ConsultationPerson & { count: number }>;
}

export interface AppointmentHistoryEntry {
  id: number;
  at: string;
  action: string;
  from: PersonalAppointmentStatus | null;
  to: PersonalAppointmentStatus | null;
  reason: string | null;
  by: ConsultationPerson | null;
}

export const listCompletedConsultations = async (
  params: CompletedConsultationFilters & { page: number; pageSize: number },
): Promise<PaginatedResponse<CompletedConsultationRow>> => {
  const { data } = await apiClient.get('/personal-appointments/completed', { params });
  return data;
};

export const getCompletedConsultationSummary = async (params: CompletedConsultationFilters): Promise<CompletedConsultationSummary> => {
  const { data } = await apiClient.get('/personal-appointments/completed/summary', { params });
  return data.data;
};

export const getCompletedConsultationFilterOptions = async (): Promise<{
  counselors: ConsultationPerson[];
  branches: Array<{ id: number; name: string }>;
}> => {
  const { data } = await apiClient.get('/personal-appointments/completed/filters');
  return data.data;
};

export const exportCompletedConsultations = async (params: CompletedConsultationFilters): Promise<Blob> => {
  const { data } = await apiClient.get('/personal-appointments/completed/export', { params, responseType: 'blob' });
  return data;
};

/** Correct a mistaken completion — back to Scheduled, with a reason. */
export const reopenPersonalAppointment = async (id: number, reason: string): Promise<PersonalAppointmentDetail> => {
  const { data } = await apiClient.post(`/personal-appointments/${id}/reopen`, { reason });
  return data.data;
};

export const getPersonalAppointmentHistory = async (id: number): Promise<AppointmentHistoryEntry[]> => {
  const { data } = await apiClient.get(`/personal-appointments/${id}/history`);
  return data.data;
};
